/**
 * Fit-verdict projections for the Test-Fit Studio — shared by the status bar
 * and the Fit Verdict rail (Option C). Everything here is a PURE PROJECTION
 * over the solver's `GenerationParcelTestFitResult`: it renders the solver's
 * containment / setback / reservation output, never recomputes geometry. No
 * per-component or per-parcel quantity is invented — only fields the solver
 * actually produced are surfaced.
 */
import type { GenerationParcelTestFitResult } from '../../../../lib/generationImpact/generationLayoutGeometry';
import { summarizeLayoutYield } from '../../../../lib/generationImpact/generationLayoutYield';

const METRES_PER_FOOT = 0.3048;

export interface FitReservationRow {
  label: string;
  value: string;
  danger?: boolean;
}

/**
 * Parcel + reservation acreage and edge-clearance readout. Moved here from
 * StudioStatusBar so the status-bar drawer and the verdict rail share one
 * source of truth.
 */
export function fitReservationRows(testFit: GenerationParcelTestFitResult): readonly FitReservationRow[] {
  const summary = testFit.plan?.reservationSummary;
  return [
    { label: 'Selected parcel (gross)', value: testFit.parcel ? `${testFit.parcel.area.toFixed(1)} ac` : 'Missing' },
    { label: 'Permanent equipment', value: summary ? `${summary.permanentEquipmentAcres.toFixed(1)} ac` : 'Invalid' },
    { label: 'Route reservation', value: summary ? `${summary.routeReservationAcres.toFixed(1)} ac` : 'Invalid' },
    { label: 'Construction laydown', value: summary ? `${summary.constructionLaydownAcres.toFixed(1)} ac` : 'Invalid' },
    {
      label: 'Minimum edge clearance',
      value: testFit.minimumParcelEdgeClearanceM == null
        ? 'Unverified'
        : `${(testFit.minimumParcelEdgeClearanceM / METRES_PER_FOOT).toFixed(1)} ft`,
      danger: testFit.parcelEdgeSetbackSatisfied === false,
    },
    {
      label: 'Entered setback',
      value: testFit.parcelEdgeSetbackSatisfied == null
        ? 'Unverified'
        : testFit.parcelEdgeSetbackSatisfied ? 'Satisfied' : 'Not satisfied',
      danger: testFit.parcelEdgeSetbackSatisfied === false,
    },
  ];
}

/** A single line in the Fit Verdict checklist. `info` never gates the fit. */
export type FitCheckState = 'pass' | 'warn' | 'unknown' | 'info';
export type FitHeadlineState = Exclude<FitCheckState, 'info'>;

export interface FitCheck {
  id: string;
  label: string;
  state: FitCheckState;
  metric: string;
}

export interface FitVerdict {
  /** Headline state after projecting every solver gate reported below. */
  headline: FitHeadlineState;
  headlineLabel: string;
  checks: readonly FitCheck[];
  /** Overall packing utilization (post-setback envelope), 0–100 or null. */
  utilizationPct: number | null;
  /** Parcel area left free of components, 0–100 or null. */
  greenReservePct: number | null;
  /** Most useful action for the first unresolved solver gate. */
  nextStep: string;
}

function containmentState(testFit: GenerationParcelTestFitResult): FitHeadlineState {
  if (testFit.status === 'fits') return 'pass';
  if (testFit.status === 'partial') return 'warn';
  return 'unknown';
}

function nextStepFor(checks: readonly FitCheck[], headline: FitHeadlineState): string {
  const unresolved = (id: string): FitCheck | undefined => checks.find(
    check => check.id === id && (check.state === 'warn' || check.state === 'unknown'),
  );

  if (unresolved('packing')) {
    return 'Increase the usable envelope or change packing controls, then rerun the fit.';
  }
  if (unresolved('collisions')) {
    return 'Resolve or verify fixed-component overlaps, then rerun the fit check.';
  }
  if (unresolved('containment')) {
    return 'Move or resize uncontained blocks, then rerun the fit check.';
  }
  if (unresolved('setback')) {
    return 'Adjust placement or setback inputs, then rerun the fit check.';
  }
  if (unresolved('exclusions')) {
    return 'Resolve or verify exclusion-zone overlap, then rerun the fit check.';
  }
  if (headline !== 'pass') {
    return 'Review the solver caveats and rerun the fit check.';
  }
  return 'Freeze this fit through Save → Promote → Publish → rebuild SED in Step 6.';
}

/**
 * Build the Fit Verdict from the solver result. Containment and setback are
 * the solver's own verdicts; acreage/exclusion lines are reported for context.
 */
export function buildFitVerdict(testFit: GenerationParcelTestFitResult): FitVerdict {
  const yieldSummary = summarizeLayoutYield(testFit);
  const solverHeadline = containmentState(testFit);

  const checks: FitCheck[] = [];

  if (testFit.plan?.status === 'overflow') {
    checks.push({
      id: 'packing',
      label: 'Packing envelope',
      state: 'warn',
      metric: testFit.plan.widthOverflowM > 0 || testFit.plan.heightOverflowM > 0
        ? `${Math.max(testFit.plan.widthOverflowM, testFit.plan.heightOverflowM).toFixed(0)} m overflow`
        : 'Fleet does not pack',
    });
  }

  // Containment — blocks fully on buildable ground vs. the full fleet.
  checks.push({
    id: 'containment',
    label: 'Containment',
    state: yieldSummary.blocksPlaced > 0
      ? (yieldSummary.blocksContained >= yieldSummary.blocksPlaced ? 'pass' : 'warn')
      : 'unknown',
    metric: yieldSummary.blocksPlaced > 0
      ? `${yieldSummary.blocksContained}/${yieldSummary.blocksPlaced} blocks`
      : testFit.containmentPct != null ? `${testFit.containmentPct.toFixed(0)}%` : '—',
  });

  // Setback — the solver's parcel-edge setback verdict.
  checks.push({
    id: 'setback',
    label: 'Setback compliance',
    state: testFit.parcelEdgeSetbackSatisfied == null
      ? 'unknown'
      : testFit.parcelEdgeSetbackSatisfied ? 'pass' : 'warn',
    metric: testFit.minimumParcelEdgeClearanceM == null
      ? 'Unverified'
      : `min ${(testFit.minimumParcelEdgeClearanceM / METRES_PER_FOOT).toFixed(0)} ft`,
  });

  // Exclusion overlap — `undefined` means no exclusion analysis was requested,
  // while `null` means an active analysis failed and must remain unresolved.
  const overlap = testFit.exclusionOverlapAcres ?? 0;
  const exclusionUnavailable = testFit.layout?.exclusions?.femaStatus === 'unavailable'
    || testFit.layout?.exclusions?.femaStatus === 'partial';
  checks.push({
    id: 'exclusions',
    label: 'Clear of exclusions',
    state: exclusionUnavailable
      ? 'unknown'
      : testFit.exclusionOverlapAcres === undefined
      ? 'info'
      : testFit.exclusionOverlapAcres === null
        ? 'unknown'
        : overlap > 0.01 ? 'warn' : 'pass',
    metric: exclusionUnavailable
      ? testFit.layout?.exclusions?.femaStatus === 'partial'
        ? `Screening incomplete${overlap > 0.01 ? `; ${overlap.toFixed(1)} ac on known exclusions` : ''}`
        : 'Analysis unavailable'
      : testFit.exclusionOverlapAcres === undefined
      ? 'Not applied'
      : testFit.exclusionOverlapAcres === null
        ? 'Unverified'
      : overlap > 0.01 ? `${overlap.toFixed(1)} ac on exclusions` : 'None',
  });

  // Fixed-component collisions are an independent schema-v2 fit gate.
  // Omitted means no fixed obstacles were active; null is a failed analysis.
  if (testFit.obstacleOverlapAcres !== undefined) {
    const obstacleOverlap = testFit.obstacleOverlapAcres ?? 0;
    checks.push({
      id: 'collisions',
      label: 'Clear of components',
      state: testFit.obstacleOverlapAcres === null
        ? 'unknown'
        : obstacleOverlap > 0.01 ? 'warn' : 'pass',
      metric: testFit.obstacleOverlapAcres === null
        ? 'Unverified'
        : obstacleOverlap > 0.01
          ? `${obstacleOverlap.toFixed(2)} ac overlap`
          : 'None',
    });
  }

  // Green reserve — parcel headroom left after placement (context only).
  if (yieldSummary.greenReservePct != null) {
    checks.push({
      id: 'reserve',
      label: 'Parcel headroom',
      state: 'info',
      metric: `${yieldSummary.greenReservePct.toFixed(0)}% free`,
    });
  }

  const headline: FitHeadlineState = solverHeadline === 'warn'
    || checks.some(check => check.state === 'warn')
    ? 'warn'
    : solverHeadline === 'unknown'
      || checks.some(check => check.state === 'unknown')
      ? 'unknown'
      : 'pass';

  return {
    headline,
    headlineLabel: headline === 'pass'
      ? 'Contained + setback'
      : headline === 'warn' ? 'Fit issue' : 'Unverified',
    checks,
    utilizationPct: yieldSummary.areaUtilizationPct,
    greenReservePct: yieldSummary.greenReservePct,
    nextStep: nextStepFor(checks, headline),
  };
}
